import {Button, Card, Dropdown, makeStyles, Option, Spinner, Title3, tokens} from "@fluentui/react-components";
import React, {useEffect, useState} from "react";
import {useApiMutation, useApiQuery} from "../../services/useApiQuery.ts";
import {useTranslation} from "react-i18next";
import {ArrowSync24Regular} from "@fluentui/react-icons";

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: '16px',
    },
    header: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        marginBottom: '16px',
        '@media (min-width: 641px)': {
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
    },
    actionRow: {
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        '@media (min-width: 641px)': {
            width: 'auto',
            justifyContent: 'flex-end',
        },
    },
    backButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
    },
    actionArea: {
        display: 'flex',
        alignItems: 'flex-end',
        gap: '16px',
        marginTop: '16px',
        padding: '16px',
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: '4px',
    },
    formField: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        minWidth: '250px',
    },
    formRow: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        marginBottom: '16px',
    },
    fieldLabel: {
        fontWeight: '500',
        marginBottom: '4px',
    },
    dropdownContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    uuidDropdown: {
        width: '350px', // Wide enough for a UUID plus some padding
        '& button': {
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
        },
        '& div[role="listbox"]': {
            width: '100%',
        },
        '& .fui-Option': {
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
        }
    },
    startButton: {
        marginLeft: 'auto',
    },
    errorContainer: {
        padding: '16px',
        backgroundColor: tokens.colorPaletteRedBackground1,
        borderRadius: '4px',
        marginBottom: '16px',
    },
    successContainer: {
        padding: '16px',
        backgroundColor: tokens.colorPaletteGreenBackground1,
        borderRadius: '4px',
        marginBottom: '16px',
    },
    responsiveTable: {
        '@media (max-width: 640px)': {
            '& table': {
                display: 'block',
                width: '100%',
            },
            '& thead': {
                display: 'none',
            },
            '& tbody': {
                display: 'block',
                width: '100%',
            },
            '& tr': {
                display: 'flex',
                flexDirection: 'column',
                padding: '12px 0',
                marginBottom: '8px',
                '&:not(:last-child)': {
                    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
                    paddingBottom: '16px',
                },
            },
            '& td': {
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                border: 'none',
                padding: '8px 0',
                width: '100%',
                position: 'relative',
                '&::before': {
                    content: 'attr(data-label)',
                    fontWeight: 'bold',
                    marginRight: '24px',
                    minWidth: '80px',
                    flexShrink: 0,
                },
            },
            '& [data-label="Description"]': {
                alignItems: 'flex-start',
                wordBreak: 'break-word',
                '& > *': {
                    maxWidth: '60%',
                    overflowWrap: 'break-word',
                    wordWrap: 'break-word',
                    hyphens: 'auto',
                },
            },
            '& [data-label="Actions"]': {
                paddingTop: '8px',
                marginTop: '8px',
            },
            '& [role="cell-actions"]': {
                opacity: '1 !important',
                // @ts-ignore
                position: 'static !important',
                transform: 'none !important',
                padding: '0 !important',
            },
        },
    },
});

// Utility function to download data as a JSON file
const downloadJsonFile = (data: any, filename: string = 'report.json') => {
    // Create a blob from the JSON data
    const blob = new Blob([JSON.stringify(data, null, 2)], {type: 'application/json'});

    // Create a URL for the blob
    const url = URL.createObjectURL(blob);

    // Create a temporary anchor element
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;

    // Append to the DOM, click to trigger download, then remove
    document.body.appendChild(a);
    a.click();

    // Clean up
    setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, 0);
};

export const ReportTab: React.FC = () => {
    const styles = useStyles();
    const {t} = useTranslation();

    const {
        data: tenants,
        isLoading: isLoadingTenants,
        isPending: isPendingTenants,
        isRefetching: isRefetchingTenants,
        refetch: refetchTenants
    } = useApiQuery<string[]>('/api/internal/tenants/list');

    const [tenantId, setTenantId] = useState<string | null>(null)
    const [campaignId, setCampaignId] = useState<string | null>(null)

    const fetchCampaignsList = useApiMutation<string[]>('POST', {
        onSuccess: data => {
            if (data && Array.isArray(data) && data.length > 0) {
                setCampaignId(data[0])
            }
        }
    });

    const fetchReport = useApiMutation('POST', {
        onSuccess: (data) => downloadJsonFile(data, `report_${tenantId}_${campaignId}_${new Date().getTime()}.json`),
    })

    useEffect(() => {
        if (tenants && tenants?.length > 0 && !tenantId) {
            setTenantId(tenants[0] || null);
        }
    }, [tenants, isPendingTenants, isRefetchingTenants, tenantId]);

    useEffect(() => {
        if (tenantId) {
            fetchCampaignsList.mutate({
                path: "/api/internal/tenants/campaigns/list",
                body: {
                    tenantId: tenantId
                }
            })
        }
    }, [tenantId]);

    const onReportGenerate = () => {
        fetchReport.mutate({
            path: "/api/internal/tenants/campaigns/report",
            body: {
                campaignId,
                tenantId
            }
        })
    }

    return <Card>
        <div className={styles.container}>
            <div className={styles.header}>
                <Title3>{t("nav.reports")}</Title3>
                <Button
                    className="ms-auto"
                    appearance="subtle"
                    icon={(isRefetchingTenants || isLoadingTenants || isPendingTenants) ?
                        <Spinner size="extra-tiny"/> : <ArrowSync24Regular/>}
                    onClick={() => {
                        refetchTenants().then(() => fetchCampaignsList.mutate({
                            path: "/api/internal/tenants/campaigns/list",
                            body: {
                                tenantId: tenantId
                            }
                        }))
                    }}
                    disabled={isRefetchingTenants || isLoadingTenants || isPendingTenants}
                >
                    {t("button.refresh")}
                </Button>
            </div>
            <div className={styles.formRow}>
                <div className={styles.fieldLabel}>
                    <label htmlFor="tenantDropdown">Tenant ID</label>
                </div>
                <div className={styles.dropdownContainer}>
                    <Dropdown
                        id="tenantDropdown"
                        multiselect={false}
                        style={{width: '350px'}}
                        value={tenantId || ""}
                        onOptionSelect={(_, data) => setTenantId(data.optionValue as string)}
                        disabled={isPendingTenants || isRefetchingTenants}
                        className={styles.uuidDropdown}
                    >
                        {tenants?.map((id) => (
                            <Option key={id} text={id} value={id}>{id}</Option>
                        ))}
                    </Dropdown>
                    {(isPendingTenants || isRefetchingTenants) && <Spinner size="tiny"/>}
                </div>
            </div>

            <div className={styles.formRow}>
                <div className={styles.fieldLabel}>
                    <label htmlFor="campaignDropdown">Campaign ID</label>
                </div>
                <div className={styles.dropdownContainer}>
                    <Dropdown
                        id="campaignDropdown"
                        style={{width: '350px'}}
                        multiselect={false}
                        defaultValue={campaignId || ""}
                        value={campaignId || ""}
                        onOptionSelect={(_, data) => setCampaignId(data.optionValue as string)}
                        disabled={fetchCampaignsList.isPending}
                    >
                        {(fetchCampaignsList.data || [])?.map((id) => (
                            <Option key={id} text={id} value={id}>{id}</Option>
                        ))}
                    </Dropdown>
                    {(fetchCampaignsList.isPending) && <Spinner size="tiny"/>}
                </div>
            </div>

            <div>
                <Button
                    disabled={!campaignId || !tenantId || fetchReport.isPending}
                    onClick={onReportGenerate}
                    appearance="primary"
                    icon={fetchReport.isPending ? <Spinner size="tiny"/> : undefined}
                >
                    {fetchReport.isPending ? "Downloading..." : "Download"}
                </Button>
            </div>
        </div>
    </Card>
};